import type { Archive } from '../data/pkg.ts';
import type { Color, Renderer, Texture } from './gl.ts';
import {
  FTL_FONTS,
  FTL_FONTS_JA,
  alignLineX,
  layoutLine,
  lineAdvance,
  parseFontFile,
  resolveFtlFontId,
  wrapLines,
  type FontFile,
  type FtlFontSpec,
  type GlyphQuad,
  type TextAlign,
} from './font-format.ts';

export type { TextAlign } from './font-format.ts';

/**
 * A .font file bound to a GL atlas and a render size. Several BitmapFonts can share one
 * file/atlas (FTL registers c&cnew at 2x and 3x, HL2 up to 4x...).
 * Text is positioned like freetype::print: `y` is the top of the first line, the baseline
 * is `y + baseline`, lines are `lineHeight` apart.
 */
export class BitmapFont {
  readonly scale: number;

  constructor(
    readonly name: string,
    readonly file: FontFile,
    readonly texture: Texture,
    /** Render size in px (scale = size / file.nativeSize). */
    readonly size: number,
    readonly baseline: number,
    readonly lineHeight: number,
    /** FTL font number, when created from the FTL font table. */
    readonly id?: number,
  ) {
    this.scale = size / file.nativeSize;
  }

  /** Same file/atlas at another size; baseline/lineHeight default to scaled native metrics. */
  resized(size: number, baseline?: number, lineHeight?: number): BitmapFont {
    const s = size / this.file.nativeSize;
    return new BitmapFont(this.name, this.file, this.texture, size, baseline ?? this.file.baseline * s, lineHeight ?? size - 4);
  }
}

export interface FontLoadOptions {
  size?: number;
  baseline?: number;
  lineHeight?: number;
}

/** `fonts/<name>.font` unless `name` already is an archive path. */
export function fontPath(name: string): string {
  return name.includes('/') && name.endsWith('.font') ? name : `fonts/${name}.font`;
}

/** Parses a .font file from the archive and uploads its atlas. */
export async function loadFont(r: Renderer, pkg: Archive, name: string, opts: FontLoadOptions = {}): Promise<BitmapFont> {
  const path = fontPath(name);
  const file = parseFontFile(await pkg.readUncached(path));
  const tex = r.createAlphaTexture(file.atlas.width, file.atlas.height, file.atlas.pixels);
  tex.path = path;
  const size = opts.size ?? file.nativeSize;
  const s = size / file.nativeSize;
  // Defaults follow freetype::font_data::init_bitmap: baseline = native baseline * scale,
  // lineHeight = size - 4.
  return new BitmapFont(path, file, tex, size, opts.baseline ?? file.baseline * s, opts.lineHeight ?? size - 4);
}

export interface TextOptions {
  align?: TextAlign;
  /** Word-wrap width in px (0 / undefined = only hard '\n' breaks). */
  maxWidth?: number;
  /** Overrides the font's line spacing. */
  lineHeight?: number;
}

const quads: GlyphQuad[] = [];

function drawLine(r: Renderer, font: BitmapFont, x: number, baseline: number, line: string, color: Color, align: TextAlign): number {
  const { file, texture: tex, scale } = font;
  quads.length = 0;
  const end = layoutLine(file, line, scale, alignLineX(file, line, scale, x, align), baseline, quads);
  const iw = 1 / file.atlas.width;
  const ih = 1 / file.atlas.height;
  for (const q of quads) {
    const g = q.glyph;
    r.quad(tex, q.x, q.y, q.w, q.h, g.x * iw, g.y * ih, (g.x + g.w) * iw, (g.y + g.h) * ih, color);
  }
  return end;
}

/**
 * Draws text with its top-left (or top-center / top-right) at (x, y). Handles '\n' and,
 * with `opts.maxWidth`, word wrapping. Returns the pen x after the last line's last glyph.
 * FTL: freetype::easy_print / easy_printCenter / easy_printRightAlign.
 */
export function drawText(r: Renderer, font: BitmapFont, x: number, y: number, text: string, color: Color, opts: TextOptions = {}): number {
  const align = opts.align ?? 'left';
  const lh = opts.lineHeight ?? font.lineHeight;
  let base = y + font.baseline;
  let end = x;
  for (const line of wrapLines(font.file, text, font.scale, opts.maxWidth ?? 0)) {
    end = drawLine(r, font, x, base, line, color, align);
    base += lh;
  }
  return end;
}

/**
 * Word-wrapped text; returns the height used (lines * line spacing).
 * FTL: freetype::easy_printAutoNewlines.
 */
export function drawTextWrapped(
  r: Renderer,
  font: BitmapFont,
  x: number,
  y: number,
  maxWidth: number,
  text: string,
  color: Color,
  lineSpacing?: number,
  align: TextAlign = 'left',
): number {
  const lh = lineSpacing ?? font.lineHeight;
  const lines = wrapLines(font.file, text, font.scale, maxWidth);
  let base = y + font.baseline;
  for (const line of lines) {
    drawLine(r, font, x, base, line, color, align);
    base += lh;
  }
  return lines.length * lh;
}

/** Width of the widest line (FTL: easy_measureWidth / font_text_width). */
export function measureText(font: BitmapFont, text: string): number {
  let w = 0;
  for (const line of text.split('\n')) w = Math.max(w, lineAdvance(font.file, line, font.scale));
  return w;
}

/** Lines after wrapping and their total height (FTL: easy_measurePrintLines). */
export function measureWrapped(font: BitmapFont, text: string, maxWidth: number, lineSpacing?: number): { lines: string[]; width: number; height: number } {
  const lines = wrapLines(font.file, text, font.scale, maxWidth);
  let width = 0;
  for (const l of lines) width = Math.max(width, lineAdvance(font.file, l, font.scale));
  return { lines, width, height: lines.length * (lineSpacing ?? font.lineHeight) };
}

/** Named FTL font numbers (the `int font` argument of freetype::easy_print). */
export const FONT = {
  NUM: 0,
  CC: 5,
  JUSTIN8: 9,
  JUSTIN10: 10,
  JUSTIN11_BOLD: 12,
  JUSTIN12_BOLD: 13,
  CCNEW_2X: 14,
  CCNEW_2X_TIGHT: 16,
  JUSTIN11_BOLD_2X: 18,
  JUSTIN11_BOLD_3X: 20,
  CCNEW_3X: 24,
  HL1: 51,
  HL1_2X: 52,
  HL1_3X: 53,
  HL1_2X_SHORT: 59,
  HL2: 61,
  HL2_2X: 62,
  HL2_3X: 63,
  HL2_4X: 64,
} as const;

export interface FontSetOptions {
  /** Also load the Japanese fonts (ids 100-107, ~57 MB of atlases). */
  japanese?: boolean;
}

/** All fonts FTL registers, addressed by FTL font number like freetype::easy_print. */
export class FontSet {
  private constructor(private readonly fonts: ReadonlyMap<number, BitmapFont>) {}

  static async load(r: Renderer, pkg: Archive, opts: FontSetOptions = {}): Promise<FontSet> {
    const specs: FtlFontSpec[] = [...FTL_FONTS, ...(opts.japanese ? FTL_FONTS_JA : [])];
    const files = new Map<string, Promise<BitmapFont | null>>();
    for (const sp of specs) {
      if (!files.has(sp.file)) {
        files.set(
          sp.file,
          pkg.has(sp.file)
            ? loadFont(r, pkg, sp.file).catch((err: unknown) => {
                console.warn(`font ${sp.file}: ${err instanceof Error ? err.message : String(err)}`);
                return null;
              })
            : Promise.resolve(null),
        );
      }
    }
    const fonts = new Map<number, BitmapFont>();
    for (const sp of specs) {
      const base = await files.get(sp.file)!;
      if (!base) continue;
      fonts.set(sp.id, new BitmapFont(sp.file, base.file, base.texture, sp.size, sp.baseline, sp.lineHeight, sp.id));
    }
    if (!fonts.has(12)) throw new Error('FTL fonts missing from ftl.dat (fonts/JustinFont11Bold.font)');
    return new FontSet(fonts);
  }

  /** FTL font number → font, with ResourceControl::GetFontData's remapping/fallback. */
  get(id: number): BitmapFont {
    return this.fonts.get(resolveFtlFontId(id, (i) => this.fonts.has(i)))!;
  }

  has(id: number): boolean {
    return this.fonts.has(id);
  }

  ids(): number[] {
    return [...this.fonts.keys()].sort((a, b) => a - b);
  }
}
